import { AlertTriangle, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { AutoFillBanner } from "@/components/onboarding/formular-notificare/AutoFillBanner";
import { FieldSelectionDialog } from "@/components/onboarding/formular-notificare/FieldSelectionDialog";
import { FieldTooltip } from "@/components/onboarding/shared/FieldTooltip";
import { ONBOARDING_TOOLTIPS } from "@/utils/onboardingTooltips";
import type { EntityData } from "@/types/entity";
import type { SizeEvaluation } from "@/types";
import type {
  FieldDiff,
  FetchCompanyDataResultStatus,
} from "@/hooks/useOnboardingForm";

type FinancialDataSectionProps = {
  entityData: Partial<EntityData>;
  autoFilledFields: Set<string>;
  sizeEvaluation: SizeEvaluation | null;
  onFieldChange: (field: keyof EntityData, value: unknown) => void;
  fieldErrors: Record<string, string>;
  cui: string;
  onFetchCompanyData: () => void;
  isFetchingCompanyData: boolean;
  fetchCompanyDataResult: FetchCompanyDataResultStatus;
  fieldDiffs: FieldDiff[];
  showFieldSelectionDialog: boolean;
  onFieldSelectionDialogChange: (open: boolean) => void;
  onFieldSelectionConfirm: (selectedFields: string[]) => void;
};

function FieldError({
  fieldName,
  errors,
}: {
  fieldName: string;
  errors: Record<string, string>;
}) {
  const error = errors[fieldName];
  if (!error) return null;

  return (
    <div className="flex items-center gap-2 mt-1">
      <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
      <span className="text-xs text-destructive">{error}</span>
    </div>
  );
}

const getSizeBadgeVariant = (
  size: string
): "secondary" | "default" | "destructive" => {
  switch (size) {
    case "MICĂ":
      return "secondary";
    case "MIJLOCIE":
      return "default";
    case "MARE":
      return "destructive";
    default:
      return "secondary";
  }
};

function FinancialDataSection({
  entityData,
  autoFilledFields,
  sizeEvaluation,
  onFieldChange,
  fieldErrors,
  cui,
  onFetchCompanyData,
  isFetchingCompanyData,
  fetchCompanyDataResult,
  fieldDiffs,
  showFieldSelectionDialog,
  onFieldSelectionDialogChange,
  onFieldSelectionConfirm,
}: FinancialDataSectionProps) {
  const fieldHighlight = (field: string) =>
    autoFilledFields.has(field)
      ? "ring-2 ring-ds-blue-300 ring-offset-1 bg-ds-blue-50/50 transition-all duration-500"
      : "transition-all duration-500";

  const handleNumberChange = (field: keyof EntityData, value: string) => {
    const num = value === "" ? 0 : Number(value);
    if (!isNaN(num)) {
      onFieldChange(field, num);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-ds-text-400">
        Câmpurile marcate cu{" "}
        <span className="text-destructive font-medium">*</span> sunt obligatorii
      </p>

      <AutoFillBanner
        cui={cui}
        variant="financial"
        isFetching={isFetchingCompanyData}
        fetchResult={fetchCompanyDataResult}
        onFetch={onFetchCompanyData}
      />

      <FieldSelectionDialog
        open={showFieldSelectionDialog}
        onOpenChange={onFieldSelectionDialogChange}
        diffs={fieldDiffs}
        onConfirm={onFieldSelectionConfirm}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label
            htmlFor="nr-salariati"
            className="inline-flex items-center gap-1"
          >
            Nr. Angajați <span className="text-destructive ml-1">*</span>
            <FieldTooltip content={ONBOARDING_TOOLTIPS.nr_salariati} />
          </Label>
          <Input
            id="nr-salariati"
            type="number"
            min={0}
            value={entityData.nr_salariati || ""}
            onChange={(e) => handleNumberChange("nr_salariati", e.target.value)}
            placeholder="ex: 150"
            className={cn(
              "text-xs md:text-sm",
              fieldHighlight("nr_salariati"),
              fieldErrors["nr_salariati"] && "border-destructive"
            )}
          />
          <FieldError fieldName="nr_salariati" errors={fieldErrors} />
        </div>

        <div>
          <Label
            htmlFor="cifra-afaceri"
            className="inline-flex items-center gap-1"
          >
            Cifra de Afaceri (EUR){" "}
            <span className="text-destructive ml-1">*</span>
            <FieldTooltip content={ONBOARDING_TOOLTIPS.cifra_afaceri} />
          </Label>
          <Input
            id="cifra-afaceri"
            type="number"
            min={0}
            value={entityData.cifra_afaceri || ""}
            onChange={(e) =>
              handleNumberChange("cifra_afaceri", e.target.value)
            }
            placeholder="ex: 5000000"
            className={cn(
              "text-xs md:text-sm",
              fieldHighlight("cifra_afaceri"),
              fieldErrors["cifra_afaceri"] && "border-destructive"
            )}
          />
          <FieldError fieldName="cifra_afaceri" errors={fieldErrors} />
        </div>

        <div>
          <Label htmlFor="active" className="inline-flex items-center gap-1">
            Active Totale (EUR) <span className="text-destructive ml-1">*</span>
            <FieldTooltip content={ONBOARDING_TOOLTIPS.active} />
          </Label>
          <Input
            id="active"
            type="number"
            min={0}
            value={entityData.active || ""}
            onChange={(e) => handleNumberChange("active", e.target.value)}
            placeholder="ex: 3000000"
            className={cn(
              "text-xs md:text-sm",
              fieldHighlight("active"),
              fieldErrors["active"] && "border-destructive"
            )}
          />
          <FieldError fieldName="active" errors={fieldErrors} />
        </div>
      </div>

      <div className="p-4 bg-ds-surface-50 rounded-xl border border-ds-border-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-ds-text-500 uppercase font-medium tracking-wider">
              Dimensiune calculată
            </p>
            <p className="text-lg font-bold text-navy-900 mt-0.5">
              {sizeEvaluation?.size || "—"}
            </p>
          </div>
          <Badge
            variant={
              sizeEvaluation
                ? getSizeBadgeVariant(sizeEvaluation.size)
                : "outline"
            }
            className="text-sm px-3 py-1.5"
          >
            {sizeEvaluation
              ? `ENTITATE ${sizeEvaluation.size}`
              : "Completați datele financiare"}
          </Badge>
        </div>
        {sizeEvaluation?.explanation && (
          <p className="text-xs text-ds-text-500 mt-2 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {sizeEvaluation.explanation}
          </p>
        )}
      </div>
    </div>
  );
}

export default FinancialDataSection;
